import { useEffect, useState } from 'react'
import { getBlob, ref } from 'firebase/storage'
import { storage } from '../libs/Firebase'

const blobCache = new Map<string, Promise<Blob | null>>()
let cacheGeneration = 0
const cacheListeners = new Set<() => void>()

const notifyCacheListeners = (): void => {
  cacheGeneration += 1
  cacheListeners.forEach((listener) => listener())
}

const isMissingOrDenied = (error: unknown): boolean => {
  const code = (error as { code?: string })?.code
  return code === 'storage/object-not-found' || code === 'storage/unauthorized'
}

const fetchProfileImageBlob = (path: string): Promise<Blob | null> => {
  const cached = blobCache.get(path)
  if (cached) {
    return cached
  }

  const promise = getBlob(ref(storage, path))
    .then((blob) => blob ?? null)
    .catch((error) => {
      if (isMissingOrDenied(error)) {
        // Do not leave a sticky null in the cache — a later seed/upload must be able to win
        blobCache.delete(path)
        return null
      }
      blobCache.delete(path)
      throw error
    })

  blobCache.set(path, promise)
  return promise
}

/** Seed the session cache after a successful upload so the UI updates without refetching. */
export const seedProfileImageCache = (path: string, blob: Blob): void => {
  blobCache.set(path, Promise.resolve(blob))
  notifyCacheListeners()
}

export const invalidateProfileImageCache = (path: string): void => {
  blobCache.delete(path)
  notifyCacheListeners()
}

export const useProfileImage = (path: string | undefined | null) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [generation, setGeneration] = useState(cacheGeneration)

  useEffect(() => {
    const onCacheChange = () => setGeneration(cacheGeneration)
    cacheListeners.add(onCacheChange)
    return () => {
      cacheListeners.delete(onCacheChange)
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const revokeCurrentUrl = () => {
      setObjectUrl((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev)
        }
        return null
      })
    }

    if (!path?.trim()) {
      revokeCurrentUrl()
      setLoading(false)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    fetchProfileImageBlob(path)
      .then(async (blob) => {
        if (cancelled) {
          return
        }
        const latest = await blobCache.get(path)
        const resolved = latest === undefined ? blob : latest
        setObjectUrl((prev) => {
          if (prev) {
            URL.revokeObjectURL(prev)
          }
          if (!resolved) {
            return null
          }
          return URL.createObjectURL(resolved)
        })
        setLoading(false)
      })
      .catch((fetchError) => {
        if (cancelled) {
          return
        }
        setError(fetchError)
        revokeCurrentUrl()
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [path, generation])

  useEffect(() => {
    return () => {
      setObjectUrl((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev)
        }
        return null
      })
    }
  }, [])

  return { objectUrl, loading, error, hasImage: !!objectUrl }
}
